import React, { useState } from 'react';
// Api
import client from '../../api/client';
// Constants
import { MAILING_LIST_UNSUBSCRIBE_API } from '../../utils/constants/ApiRoutes';
// Components
import LoadingSpinner from '../utils/LoadingSpinner';

function UnsubscribeForm() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [retryStatus, setRetryStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  const handleChange = (e) => {
    setSubmitError(null);
    setRetryStatus(null);
    setEmail(e.target.value);
  };

  const runUnsubscribe = () =>
    client
      .delete(`${MAILING_LIST_UNSUBSCRIBE_API}/${email}`, false)
      .then(() => {
        setSubmitSuccess(true);
        setSubmitError(null);
        setRetryStatus(null);
        setEmail('');
      })
      .catch((err) => {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          'Unsubscribe failed. Please try again.';
        setSubmitError(msg);
        setSubmitSuccess(false);
        setRetryStatus('error');
      });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    setRetryStatus(null);

    runUnsubscribe().finally(() => {
      setIsSubmitting(false);
    });
  };

  const handleRetry = () => {
    setRetryStatus('loading');
    runUnsubscribe()
      .then(() => setRetryStatus('success'))
      .catch(() => setRetryStatus('error'));
  };

  return (
    <form
      className="grid gap-y-4 md:gap-y-6 w-full"
      onSubmit={handleSubmit}
      aria-busy={isSubmitting || retryStatus === 'loading' ? 'true' : 'false'}
    >
      <div className="grid">
        <label
          htmlFor="email"
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Enter your email to unsubscribe
        </label>
        <input
          type="email"
          name="email"
          id="email"
          className="bg-colour9 w-full text-colour2 rounded-md py-1 px-1"
          placeholder="your@email.com"
          value={email}
          onChange={handleChange}
          required
          aria-invalid={submitError ? 'true' : 'false'}
        />
      </div>

      <div className="grid h-fit w-full">
        <button
          type="submit"
          className="bg-colour8 text-colour1 py-1 rounded-md hover:brightness-110 active:scale-95 active:brightness-90 disabled:opacity-60"
          aria-label="Unsubscribe from mailing list"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex justify-center">
              <LoadingSpinner xs={true} />
            </div>
          ) : (
            <span>Unsubscribe</span>
          )}
        </button>
      </div>

      {submitSuccess && (
        <div className="text-green-600 text-center font-semibold" role="status">
          <p>✅ You have been unsubscribed.</p>
          <p className="text-sm opacity-80">
            You’ll no longer receive emails from us. If this was a mistake, you can subscribe again anytime.
          </p>
        </div>
      )}

      {submitError && (
        <div className="grid gap-2 justify-items-center">
          <div
            className="text-error-red text-center font-semibold"
            role="alert"
            aria-live="assertive"
          >
            {submitError}
          </div>

          <button
            type="button"
            onClick={handleRetry}
            disabled={retryStatus === 'loading'}
            className="text-sm underline hover:opacity-80 disabled:opacity-50"
            aria-label="Retry unsubscribe request"
          >
            {retryStatus === 'loading' ? (
              <span className="inline-grid place-items-center">
                <LoadingSpinner xs={true} />
              </span>
            ) : (
              'Retry unsubscribe'
            )}
          </button>

          {retryStatus === 'success' && (
            <p className="text-green-600 text-sm" role="status">✅ Your unsubscribe request was successful.</p>
          )}
          {retryStatus === 'error' && (
            <p className="text-error-red text-sm" role="alert">❌ Failed to process unsubscribe. Please try again shortly.</p>
          )}
        </div>
      )}
    </form>
  );
}

export default UnsubscribeForm;
